import type {
	InferPrimitiveType,
	PrimitiveTypeByName,
	PrimitiveTypeName,
} from '#layers/data-model/types/schema/primitive-type';
import { Temporal } from 'temporal-polyfill';
import { isZonedDateTime } from '#layers/base/public';
import { PRIMITIVE_TYPE_DATE, PRIMITIVE_TYPE_DATE_TIME, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, PRIMITIVE_TYPE_DECIMAL, PRIMITIVE_TYPE_INT64, PRIMITIVE_TYPE_TIME, PRIMITIVE_TYPE_UINT64 } from '#layers/data-model/constants/schema/primitive-type';
import { parseDecimalValue } from '#layers/data-model/utils/schema/decimal-value';
import { parseInteger64Value } from '#layers/data-model/utils/schema/integer-64';

interface FieldParserInfo<T = any> {
	parse?: (value: unknown) => T | null;
	// RStore can serialize a temporal value before hydration, while it is still a wire string.
	serialize?: (value: T | string | null) => unknown;
}

type FieldParserMap = Partial<{
	[K in PrimitiveTypeName]: FieldParserInfo<InferPrimitiveType<PrimitiveTypeByName<K>>>
}>;

const FIELD_PARSERS: FieldParserMap = {
	[PRIMITIVE_TYPE_DECIMAL]: { parse: (value) => parseDecimalValue(value) ?? null },
	[PRIMITIVE_TYPE_INT64]: { parse: (value) => parseInteger64Value(value, PRIMITIVE_TYPE_INT64) ?? null },
	[PRIMITIVE_TYPE_UINT64]: { parse: (value) => parseInteger64Value(value, PRIMITIVE_TYPE_UINT64) ?? null },
	[PRIMITIVE_TYPE_DATE]: {
		parse: (value) => {
			if (value == null) return null;
			if (value instanceof Temporal.PlainDate) return value;
			if (typeof value !== 'string') return null;

			try {
				return Temporal.PlainDate.from(value);
			}
			catch {
				return null;
			}
		},
		serialize: (value) => value == null ? null : value.toString(),
	},
	[PRIMITIVE_TYPE_TIME]: {
		parse: (value) => {
			if (value == null) return null;
			if (value instanceof Temporal.PlainTime) return value;
			if (typeof value !== 'string') return null;

			try {
				return Temporal.PlainTime.from(value);
			}
			catch {
				return null;
			}
		},
		serialize: (value) => value == null ? null : value.toString(),
	},
	[PRIMITIVE_TYPE_DATE_TIME]: {
		parse: (value) => {
			if (value == null) return null;
			if (value instanceof Temporal.PlainDateTime) return value;
			if (typeof value !== 'string') return null;

			try {
				return Temporal.PlainDateTime.from(value);
			}
			catch {
				return null;
			}
		},
		serialize: (value) => value == null ? null : value.toString(),
	},
	[PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE]: {
		parse: (value) => {
			if (value == null) return null;
			if (isZonedDateTime(value)) return value;
			if (typeof value !== 'string') return null;

			try {
				const instant = Temporal.Instant.from(value);
				return instant.toZonedDateTimeISO('UTC');
			}
			catch {
				return null;
			}
		},
		serialize: (value) => {
			if (value == null) return null;
			if (typeof value === 'string') return value;

			if (!isZonedDateTime(value)) {
				return String(value);
			}

			const utc = value.withTimeZone('UTC');
			return utc.toString().replace(/\[.*\]$/, ''); // remove the timezone annotation
		},
	},
};

export function resolveFieldParser<TName extends PrimitiveTypeName>(type: TName): FieldParserInfo<InferPrimitiveType<PrimitiveTypeByName<TName>>> | undefined {
	return FIELD_PARSERS[type] as FieldParserInfo<InferPrimitiveType<PrimitiveTypeByName<TName>>> | undefined;
}
